import { Box, Button, HStack, Input, Text } from '@chakra-ui/react'
import { type FormEvent, useEffect, useState } from 'react'

import { Field, Modal } from '@mangovoice/mango-ui'

import { useDevicesI18n } from '../i18n'

interface DeviceSetDesiredFirmwareDialogProps {
  isOpen: boolean
  onClose: () => void
  selectedCount: number
  onConfirm: (desiredFirmwareVersion: string) => void
  isSubmitting: boolean
}

export function DeviceSetDesiredFirmwareDialog({
  isOpen,
  onClose,
  selectedCount,
  onConfirm,
  isSubmitting,
}: DeviceSetDesiredFirmwareDialogProps) {
  const { text } = useDevicesI18n()
  const [version, setVersion] = useState('')

  useEffect(() => {
    if (isOpen) {
      setVersion('')
    }
  }, [isOpen])

  const handleConfirm = (e: FormEvent) => {
    e.preventDefault()
    const trimmed = version.trim()
    if (!trimmed) return
    onConfirm(trimmed)
  }

  return (
    <Modal
      open={isOpen}
      onOpenChange={(e) => !e.open && !isSubmitting && onClose()}
      title={text('devices.setFirmware.title', 'Set desired firmware version')}
      maxWidth="lg"
      closeOnInteractOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
      footer={
        <HStack justify="flex-end" gap={2}>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            {text('devices.common.cancel', 'Cancel')}
          </Button>
          <Button
            type="submit"
            form="device-set-desired-firmware-form"
            colorPalette="brand"
            loading={isSubmitting}
            disabled={!version.trim()}
          >
            {text('devices.setFirmware.confirmButton', 'Set and reload')}
          </Button>
        </HStack>
      }
    >
      <form id="device-set-desired-firmware-form" onSubmit={handleConfirm}>
        <Box display="flex" flexDirection="column" gap={4}>
          <Text color="fg.muted" fontSize="sm">
            {text(
              'devices.setFirmware.description',
              'Set the desired firmware version for {{count}} selected device(s). Configuration will be reloaded after the update.',
              { count: selectedCount },
            )}
          </Text>
          <Field.Root required>
            <Field.Label>
              {text('devices.setFirmware.label', 'Desired firmware version')}
            </Field.Label>
            <Input
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder={text(
                'devices.setFirmware.placeholder',
                'e.g. 1.2.3.4',
              )}
              fontFamily="mono"
              disabled={isSubmitting}
            />
          </Field.Root>
        </Box>
      </form>
    </Modal>
  )
}
